'use client';

/**
 * Client island for /lens-calculator.
 *
 * Wires up the static form (rendered server-side in `PageContent.tsx`)
 * to the lens-suggestion formula from the original Magento inline
 * `doit()` script (the second/final definition in the source):
 *
 *   eql_mm = ((D / W) * SIZE * 100) / 98, rounded to 1 decimal place
 *
 * Where:
 *   - D = distance to target (ft)
 *   - W = width of target view (ft)
 *   - SIZE = CCD chip-size multiplier (1/4" = 3.2, 1/3" = 4.8, 1/2" = .5)
 *
 * The static form already includes `name`/`id` attributes that the parity
 * gate sees; this client component is mounted on top of the same DOM and
 * attaches a button handler to compute the result.
 */
import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { ccdOptions, labels } from './page-content';
import styles from './page.module.css';
interface LensCalculatorClientProps {
  distanceInputId: string;
  widthInputId: string;
  sizeSelectId: string;
  resultDisplayId: string;
  resultHiddenId: string;
}
export function LensCalculatorClient(t0) {
  const $ = _c(9);
  const {
    distanceInputId,
    widthInputId,
    sizeSelectId,
    resultDisplayId,
    resultHiddenId
  } = t0;
  const [result, setResult] = useState(null);
  let t1;
  if ($[0] !== distanceInputId || $[1] !== resultDisplayId || $[2] !== resultHiddenId || $[3] !== sizeSelectId || $[4] !== widthInputId) {
    t1 = () => {
      if (typeof document === "undefined") {
        return;
      }
      const distanceInput = document.getElementById(distanceInputId) as HTMLInputElement | null;
      const widthInput = document.getElementById(widthInputId) as HTMLInputElement | null;
      const sizeSelect = document.getElementById(sizeSelectId) as HTMLSelectElement | null;
      const eqlHidden = document.getElementById(resultHiddenId) as HTMLInputElement | null;
      const resultDisplay = document.getElementById(resultDisplayId);
      if (!distanceInput || !widthInput || !sizeSelect) {
        return;
      }
      const distance = Number.parseFloat(distanceInput.value);
      const width = Number.parseFloat(widthInput.value);
      const size = Number.parseFloat(sizeSelect.value);
      if (!Number.isFinite(distance) || !Number.isFinite(width) || width === 0) {
        return;
      }
      const ttl3 = Math.round(distance / width * size * 100) / 98;
      const formatted = `${ttl3.toFixed(1)} mm`;
      if (eqlHidden) {
        eqlHidden.value = formatted;
      }
      if (resultDisplay) {
        resultDisplay.textContent = formatted;
      }
      setResult(formatted);
    };
    $[0] = distanceInputId;
    $[1] = resultDisplayId;
    $[2] = resultHiddenId;
    $[3] = sizeSelectId;
    $[4] = widthInputId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleCalculate = t1;
  const t2 = result ? resultDisplayId : undefined;
  let t3;
  if ($[6] !== handleCalculate || $[7] !== t2) {
    t3 = <input type="button" value={labels.calculateValue} onClick={handleCalculate} className={`bs-btn bs-btn-primary bs-btn-large ${styles.calculateButton}`} aria-describedby={t2} />;
    $[6] = handleCalculate;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export function LensCalculatorOptions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <>{ccdOptions.map(_temp)}</>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(option) {
  return <option key={option.value} value={option.value} selected={option.selected}>{option.label}</option>;
}